import * as Yup from 'yup';
import { ValidationError } from 'yup';

import { NumberConstraintEnum } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { useResolvableQuantitySchema } from '../../ResolvableField/hooks/useResolvableQuantitySchema.hook';
import { useResolvableServiceSchema } from '../../ResolvableService/hooks/useResolvableServiceSchema.hook';

import { ResolvableServiceUsageSettingsSchema } from '../schemas/resolvable-service-usage-settings.schema';
import { ServiceUsageOriginSchema } from '../schemas/service-usage-origin.schema';

export const useResolvableServiceUsageSchema = () => {
  const { contentOriginsCodeMap } = useReferencesContext();

  const ResolvableQuantitySchema = useResolvableQuantitySchema(
    NumberConstraintEnum.Positive,
  );

  const ResolvableServiceSchema = useResolvableServiceSchema();

  const ResolvableServiceUsageSchema = Yup.object()
    .shape({
      contentOriginId: Yup.string().required(
        'Origine de la prestation requise',
      ),
      serviceUsageOrigin: ServiceUsageOriginSchema.optional()
        .nullable()
        .default(undefined),
      serviceOriginId: Yup.string(),
      serviceId: Yup.string().optional().nullable().default(undefined),
      specialService: ResolvableServiceSchema.optional()
        .nullable()
        .default(undefined),
      quantity: ResolvableQuantitySchema.optional()
        .nullable()
        .default(undefined),
      settings: ResolvableServiceUsageSettingsSchema.optional()
        .nullable()
        .default(undefined),
    })
    .test(
      'test-content-origin',
      'Origine de la prestation invalide',
      function (value) {
        const findError = () => {
          switch (value.contentOriginId) {
            case contentOriginsCodeMap.Catalog._id: {
              if (
                !!value.serviceUsageOrigin &&
                !value.serviceOriginId &&
                !value.serviceId &&
                !value.specialService &&
                !value.quantity &&
                !value.settings
              ) {
                return null;
              }

              return new ValidationError(
                "Les informations fournies ne sont pas cohérentes avec l'origine de la prestation.",
                value.serviceOriginId,
                'serviceOriginId',
              );
            }
            case contentOriginsCodeMap.Modified._id:
            case contentOriginsCodeMap.Special._id: {
              if (
                (contentOriginsCodeMap.Special._id === value.contentOriginId ||
                  !!value.serviceUsageOrigin) &&
                !!value.serviceOriginId &&
                !!(value.serviceId || value.specialService) &&
                !!value.quantity &&
                !!value.settings
              ) {
                return null;
              }

              return new ValidationError(
                "Les informations fournies ne sont pas cohérentes avec l'origine de la prestation.",
                value.serviceOriginId,
                'serviceOriginId',
              );
            }
            default:
              return false;
          }
        };

        const error = findError();

        if (!error) {
          return true;
        }

        return this.createError({
          message: () => error,
        });
      },
    )
    .test(
      'test-service-origin',
      'Origine du service invalide',
      function (value) {
        if (!value.serviceOriginId) {
          return true;
        }

        const findError = () => {
          switch (value.serviceOriginId) {
            case contentOriginsCodeMap.Catalog._id: {
              if (!!value.serviceId && !value.specialService) {
                return null;
              }

              return new ValidationError(
                'Veuillez choisir un service de référence.',
                value.serviceOriginId,
                'serviceOriginId',
              );
            }
            case contentOriginsCodeMap.Modified._id: {
              if (value.serviceId && value.specialService) {
                return null;
              }

              return new ValidationError(
                'Veuillez choisir un service de référence à modifier.',
                value.serviceOriginId,
                'serviceOriginId',
              );
            }
            case contentOriginsCodeMap.Special._id: {
              if (!value.serviceId && !!value.specialService) {
                return null;
              }

              return new ValidationError(
                'Veuillez définir un service spécial. Par ailleurs, vous ne pouvez choisir un service de référence.',
                value.serviceOriginId,
                'serviceOriginId',
              );
            }
            case undefined:
            default:
              return new ValidationError(
                'Cette information est obligatoire.',
                value.serviceOriginId,
                'serviceOriginId',
              );
          }
        };

        const error = findError();

        if (!error) {
          return true;
        }

        return this.createError({
          message: () => error,
        });
      },
    );

  return ResolvableServiceUsageSchema;
};
